import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Headset, MagnifyingGlass, User, Phone, Envelope, ArrowRight, Package, ShieldCheck } from "@phosphor-icons/react";

export default function AssistancePage() {
  useDocumentTitle("Assistenze");
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (query = "") => {
    setLoading(true);
    const { data } = await api.get("/customers", { params: query ? { q: query } : {} });
    setCustomers(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="assistance-page">
      <header className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// SUPPORTO</div>
        <h1 className="text-4xl font-display font-bold tracking-tight flex items-center gap-3">
          <Headset size={30} weight="duotone" className="text-primary" />
          Assistenze
        </h1>
        <p className="text-muted-foreground mt-2">
          Registra gli interventi di assistenza per cliente. Scala i minuti dal pacchetto oppure registra assistenze one-off con prezzo libero.
        </p>
      </header>

      <Card className="p-4 border-border/70 rounded-lg mb-4">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Cerca cliente per nome, cognome, telefono…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="assistance-search"
          />
        </div>
      </Card>

      {loading ? (
        <div className="text-center text-muted-foreground py-8">Caricamento…</div>
      ) : customers.length === 0 ? (
        <div className="text-center text-muted-foreground py-8">Nessun cliente trovato</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.map((c) => (
            <Link to={`/assistenze/${c.id}`} key={c.id} data-testid={`assistance-customer-${c.id}`} className="block">
              <Card className="p-5 border-border/70 rounded-lg hover:shadow-md hover:-translate-y-0.5 h-full"
                    style={{ transition: "transform 0.2s ease, box-shadow 0.2s ease" }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 text-primary grid place-items-center">
                    <User size={18} weight="duotone" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{c.surname} {c.name}</div>
                  </div>
                  <ArrowRight size={16} className="text-muted-foreground" />
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone size={14} /> <span className="font-mono">{c.phone}</span>
                  </div>
                  {c.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Envelope size={14} /> <span className="truncate">{c.email}</span>
                    </div>
                  )}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
